import { List } from '@mui/material';
import {
	Box,
	Typography,
	CircularProgress,
	ListItem,
	ListItemText,
	ListItemIcon,
	Chip,
	Button,
	ListItemSecondaryAction,
	Tooltip,
	Alert,
} from '@mui/material';
import { InsertDriveFile, Folder, Block, Check } from '@mui/icons-material';
import { last, range } from 'ramda';
import { getFile, getErrorIndices } from '../../../utils';
import { fileAdded, filesAdded, fileUpdated } from '../../../utils/activityStreams';
import UploadRow from './UploadRow';
import { Dispatch } from 'redux';
import { ReduxComponentInterface } from 'edocatbase/Global';
import { createTheme, ThemeProvider, lighten } from '@mui/material/styles';
import { uploadFile, uploadNewVersion, updateActivity } from '../../../actions';
import getEntriesFromFiles from '../../../../../../document-library/utils/upload';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { toggleModal } from '../../../../ModalTest/actions';
import Modal from '../../../../ModalTest';

const theme = createTheme({
	components: {
		MuiList: {
			styleOverrides: {
				root: {
					icon: {
						minWidth: '40px',
					},
				},
			},
		},
		MuiListItemText: {
			styleOverrides: {
				secondary: {
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
				},
				primary: {
					whiteSpace: 'nowrap',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
				},
			},
		},
	},
});

function CircularProgressWithLabel(props) {
	return (
		<Box className="ProgressBox">
			<CircularProgress variant="determinate" {...props} />
			<Box className="ProgressModalBar">
				<Typography
					variant="caption"
					component="div"
					style={{
						color: theme.palette.info.dark,
					}}
				>{`${Math.round(props.value)}%`}</Typography>
			</Box>
		</Box>
	);
}

type NewVersionData = {
	nodeRef: string;
	fileName: string;
	majorVersion: boolean;
	desc: string;
};

interface UploadModalProps extends ReduxComponentInterface {
	disallowedExtensions: string[];
	parentNodeRef: string;
	site: string;
	path: string;
	items: {
		files: any[];
	};
	name: string;
	callback: Function;
	uploadAction: Function;
	isOpen: boolean;
}

interface UploadModalState {
	files: any[];
	uploading: boolean;
	nodeRefs: any[];
	doneFiles: number;
	totalFiles: number;
	scanning: boolean;
	siteErrors: {
		name: string;
		path: string;
	}[];
	fileErrors: String[];
	actualDoc: [];
	anchor: any;
}

class UploadModal extends window.React.PureComponent<UploadModalProps, UploadModalState> {
	timeout = 0;

	constructor(props: UploadModalProps) {
		super(props);
		this.state = {
			files: [],
			nodeRefs: [],
			uploading: false,
			scanning: true,
			doneFiles: 0,
			totalFiles: 0,
			siteErrors: [],
			fileErrors: [],
			actualDoc: [],
			anchor: null,
		};
	}

	async componentDidMount() {
		const { items } = this.props;
		if (items) {
			const dtos = items.files;
			let newFiles = [];
			let siteErrors: {
				name: string;
				path: string;
			}[] = [];
			if (dtos[0].webkitGetAsEntry) {
				//console.log(dtos);
				await getEntriesFromFiles(dtos, siteErrors).then(response => (newFiles = response));
				//console.log(newFiles, siteErrors);
			} else {
				dtos.forEach(item => newFiles.push(item.getAsFile ? item.getAsFile() : item));
			}
			if (newFiles.length > 0) {
				this.setState(
					() => ({
						files: newFiles.map(file => ({
							file,
							excluded: false,
							done: false,
							error: '',
							progress: -1,
						})),
						totalFiles: newFiles.length,
						doneFiles: 0,
						scanning: false,
						siteErrors,
					}),
					() => this.onUpload(true, range(0, newFiles.length))
				);
			}
		}
	}

	onMenuOpen = (event: React.ChangeEvent) => {
		this.setState({
			anchor: event.currentTarget,
		});
	};

	onMenuClose = () => {
		this.setState({
			anchor: null,
		});
	};

	onUpload = (fileExistCheck: boolean, indices: number[], newVersionData?: NewVersionData) => {
		this.setState(
			state => ({
				files: state.files.map((item, index) =>
					indices.includes(index) ? { ...item, excluded: false, done: false, error: '' } : item
				),
				uploading: true,
				doneFiles: state.files.filter((item, index) => !indices.includes(index) && item.done).length,
			}),
			async () => {
				for (let i = 0; i < indices.length; i++) {
					await this.processFile(indices[i], indices.length, fileExistCheck, newVersionData);
				}

				this.setState({
					uploading: false,
				});

				if (this.state.files.every(item => item.done)) {
					this.timeout = window.setTimeout(this.onModalClose, 3000);
				}
			}
		);
	};

	processFile = async (i: number, length: number, fileExistCheck: boolean, newVersionData?: NewVersionData) => {
		const { files } = this.state;
		const { site, path, disallowedExtensions, parentNodeRef, uploadAction } = this.props;

		const item = files[i];
		const fileError = this.state.fileErrors;

		let status = 'done';
		let value: boolean | string = true;

		if (disallowedExtensions?.includes(last(item.file.name.split('.')))) {
			status = 'excluded';
			value = true;
		} else {
			const filePath = item?.file?.fullPath ? item?.file?.fullPath.replace(item?.file?.name ?? '', '') : '';
			const fullPath = path + filePath;
			const resolvedFile = item.file?.file ? await getFile(item?.file, fileError) : item?.file;

			const onProgress = (event: any) => {
				this.setState(prevState => ({
					files: [
						...prevState.files.slice(0, i),
						{
							...prevState.files[i],
							progress: Math.ceil((event.loaded / event.total) * 100),
						},
						...prevState.files.slice(i + 1),
					],
					actualDoc: prevState.files[i]?.file,
				}));
			};
			try {
				if (uploadAction && resolvedFile) {
					await uploadAction(resolvedFile, onProgress).then((response: any) => {
						response && this.state.nodeRefs.push(response.value.nodeRef.childRef);
					});
				} else if (resolvedFile) {
					let response;

					if (newVersionData) {
						response = await uploadNewVersion(
							resolvedFile,
							newVersionData.nodeRef,
							newVersionData.majorVersion,
							newVersionData.desc,
							onProgress
						);
					} else {
						response = await uploadFile(resolvedFile, site, fullPath, fileExistCheck, onProgress, parentNodeRef);
					}

					parentNodeRef && length > 4 && i + 1 === length && updateActivity(filesAdded(length, parentNodeRef));

					response?.nodeRef &&
						length < 5 &&
						updateActivity(
							newVersionData
								? fileUpdated(newVersionData.fileName, newVersionData.nodeRef)
								: fileAdded(response.fileName, response.nodeRef)
						);
				}

				status = 'done';
				value = true;
			} catch (e) {
				status = 'error';
				value = e || 'error';
			}
		}

		this.setState(state => ({
			files: [...state.files.slice(0, i), { ...item, [status]: value }, ...state.files.slice(i + 1)],
			doneFiles: state.doneFiles + 1,
			fileErrors: fileError,
		}));
	};

	cancelUploading = () => {
		window.location.reload();
	};

	onModalClose = () => {
		const { actions, name, callback } = this.props;
		const { nodeRefs } = this.state;

		if (this.timeout) {
			window.clearTimeout(this.timeout);

			this.timeout = 0;
		}

		actions.toggleModal(false, name);

		if (callback) {
			if (nodeRefs.length > 0) {
				callback(nodeRefs, true);
			} else {
				callback();
			}
		}
	};

	render() {
		const { files, uploading, doneFiles, totalFiles, scanning, siteErrors, actualDoc } = this.state;
		const { name } = this.props;
		return (
			<ThemeProvider theme={theme}>
				<Modal
					name={name}
					title={window.Alfresco.messages.global['edocat.doc-lib.uploadNewDocs']}
					onClose={this.onModalClose}
					disableBackdropClick={true}
					disableEscapeKeyDown={uploading}
					className={'UploadModal'}
				>
					<div className="modal-body">
						<Box className="UploadStatus">
							{scanning && (
								<Box component={'p'}>{window.Alfresco.messages.global['edocat.upload.loading-content']}</Box>
							)}
							{!scanning &&
								(uploading ||
									siteErrors.length > 0 ||
									files.filter(item => item.error || item.excluded).length > 0) && (
									<Box
										className="Content Current"
										style={{
											background: lighten(theme.palette.primary.light, 0.65),
											borderColor: lighten(theme.palette.primary.light, 0.4),
										}}
									>
										<List className={'BoxList'} dense={true} disableGutters>
											<ListItem disablePadding>
												<ListItemIcon sx={{ minWidth: '60px', textAlign: 'center' }}>
													<InsertDriveFile sx={{ m: '0 auto' }} />
												</ListItemIcon>
												<Tooltip title={actualDoc?.name} placement="top" arrow>
													<ListItemText
														primary={actualDoc?.name}
														secondary={`${window.Alfresco.messages.global['edocat.upload.doneText']} ${doneFiles} ${window.Alfresco.messages.global['edocat.upload.fromText']} ${totalFiles} - ${Math.ceil((doneFiles / totalFiles) * 100)}%`}
													/>
												</Tooltip>
											</ListItem>
											<ListItemSecondaryAction>
												<CircularProgressWithLabel
													value={
														files.filter(
															item => item.progress !== -1 && !(item.done || item.error || item.excluded)
														)?.[0]?.progress ?? 0
													}
												/>
											</ListItemSecondaryAction>
										</List>
									</Box>
								)}
							{!uploading &&
								doneFiles === totalFiles &&
								!(siteErrors.length > 0 || files.filter(item => item.error || item.excluded).length > 0) && (
									<Box>
										<Alert
											icon={<Check fontSize="inherit" />}
											severity="success"
											sx={{
												background: lighten(theme.palette.success.light, 0.65),
												border: `1px solid ${lighten(theme.palette.success.main, 0.4)}`,
												color: 'black',
												fontWeight: 500,
												display: 'flex',
												alignItems: 'center',
											}}
										>
											<strong>{window.Alfresco.messages.global['edocat.upload.successful.done']}</strong>
											<p>{`${window.Alfresco.messages.global['edocat.upload.doneText']} ${doneFiles} ${window.Alfresco.messages.global['edocat.upload.fromText']} ${totalFiles}`}</p>
										</Alert>
									</Box>
								)}
							{siteErrors.length > 0 && (
								<Box className="Content">
									<Typography variant="h6" component="h2">
										<Chip
											label={siteErrors.length}
											className="ChipError"
											style={{
												backgroundColor: '#f44336',
												color: theme.palette.error.contrastText,
											}}
										/>
										{window.Alfresco.messages.global['edocat.upload.repositories-error']}
									</Typography>
									<List className={'BoxList ErrorList'} dense={true} disableGutters>
										{siteErrors.map((item, index) => {
											return (
												<ListItem disablePadding key={index}>
													<ListItemIcon
														style={{
															minWidth: '40px',
															color: '#f44336',
														}}
													>
														<Folder />
													</ListItemIcon>
													<Tooltip title={item.path} placement="top" arrow>
														<ListItemText primary={item.name} />
													</Tooltip>
												</ListItem>
											);
										})}
									</List>
								</Box>
							)}
							{files.filter(item => item.error || item.excluded).length > 0 && (
								<Box className="Content">
									<Box className="UserDescision">
										<Box>
											<Typography variant="h6" component="h2">
												{window.Alfresco.messages.global['edocat.upload.user-decision']}
											</Typography>
											<Typography variant="caption" component="p" sx={{ color: theme.palette.text.secondary }}>
												{window.Alfresco.messages.global['edocat.upload.error-info']}
											</Typography>
										</Box>
										<Box>
											{files.filter(item => item.error || item.excluded).length > 1 && (
												<Button
													variant="outlined"
													size="small"
													disabled={uploading}
													className="UploadBulkAction"
													onClick={() => this.onUpload(false, getErrorIndices(files))}
												>
													{window.Alfresco.messages.global['edocat.upload.rename']}
													<Chip
														label={files.filter(item => item.error || item.excluded).length}
														sx={{
															backgroundColor: theme.palette.info.main,
															color: theme.palette.info.contrastText,
															ml: 1,
															position: 'relative',
															top: '-1px',
														}}
														size="small"
													/>
												</Button>
											)}
										</Box>
										{/* {window.Alfresco.messages.global['edocat.upload.documents-error']} */}
									</Box>
								</Box>
							)}
						</Box>
						{files.filter(item => item.error || item.excluded).length > 0 && (
							<Box>
								<List className={'BoxList ErrorList'} dense={true} disableGutters>
									{/* {files.filter((item, index) => item.error || item.excluded ? {...item, index: index} : false).map((item) => {
                      console.log(item);
                      return <UploadRow key={`uploadModalRow${item.index}`} item={item} index={item.index} onUpload={this.onUpload} />
                    })} */}

									{files.map((item, index) => {
										return item.error || item.excluded ? (
											<UploadRow
												key={`uploadModalRow${index}`}
												item={item}
												index={index}
												onUpload={this.onUpload}
												uploading={uploading}
												count={index + 1}
											/>
										) : null;
									})}
								</List>
							</Box>
						)}
					</div>
					<div className="modal-actions">
						<Box className="modalControls">
							{uploading && (
								<Button
									startIcon={<Block />}
									disableElevation
									variant="contained"
									color="error"
									onClick={() => this.cancelUploading()}
								>
									{window.Alfresco.messages.global['edocat.upload.cancel-upload']}
								</Button>
							)}
							{!uploading && (
								<Button disableElevation variant="contained" color="error" onClick={() => this.onModalClose()}>
									{window.Alfresco.messages.global['button.close']}
								</Button>
							)}
						</Box>
					</div>
				</Modal>
			</ThemeProvider>
		);
	}
}

const mapStateToProps = (state: any, props: UploadModalProps) => ({
	items: state.modal[props.name]?.items,
	isOpen: state.modal[props.name]?.isOpen,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	actions: bindActionCreators({ toggleModal }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(UploadModal);
